import { get, find, camelCase, reduce } from "lodash";

export const booleanValue = (val: unknown) => {
  if (val) {
    return (
      val === true ||
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- accepts "true"/"TRUE" string variants; non-string objects stringify to "[object Object]" and correctly fail the check
      String(val).toLowerCase() === "true"
    );
  }
  return false;
};

export default { booleanValue };

//Admin Approval Helpers

//Metadata helper to get the value of a key in the metadataItems array
interface MetadataItem {
  name?: string | null;
  value?: unknown;
}

export const getMetaValue = (
  metadataItems: MetadataItem[] | null | undefined,
  key: string
) => {
  const normalizedKey = camelCase(key);
  const item = find(
    metadataItems,
    (i) => camelCase(i?.name || "") === normalizedKey
  );
  return get(item, "value", null);
};

// Returns the uppercase initial of a string.
export const getInitial = (str: string | null | undefined) => {
  if (!str) return "";
  return str.trim().charAt(0).toUpperCase();
};

// Returns initials from first and last name strings.
export const getInitials = (
  firstName: string | null | undefined,
  lastName: string | null | undefined
) => {
  return `${getInitial(firstName)}${getInitial(lastName)}`;
};

// Detects field-level changes between two objects.
export const detectFieldChanges = (
  oldObj: object,
  newObj: object,
  fields: Record<string, string>
) => {
  return reduce(
    fields,
    (changes: string[], label, key) => {
      const oldValue = (get(oldObj, key, "") || "").trim();
      const newValue = (get(newObj, key, "") || "").trim();

      if (oldValue !== newValue) {
        let message;

        if (!oldValue && newValue) {
          message = `${label}: Added "${newValue}"`;
        } else if (oldValue && !newValue) {
          message = `${label}: Removed "${oldValue}"`;
        } else if (newValue.includes(oldValue)) {
          const added = newValue.replace(oldValue, "").trim();
          message = added
            ? `${label}: Added "${added}"`
            : `${label}: Changed to "${newValue}"`;
        } else {
          message = `${label}: Changed from "${oldValue}" to "${newValue}"`;
        }

        changes.push(message);
      }

      return changes;
    },
    []
  );
};

interface KvPair {
  key: string;
  value: unknown;
}

interface KvTransformers {
  transformKey: (k: string) => string;
  transformValue: (v: unknown) => unknown;
}

export const kvPairs = (
  obj: object | unknown[] | null | undefined,
  transformers: KvTransformers = {
    transformKey: (k) => k,
    transformValue: (v) => v,
  }
): KvPair[] => {
  /*
  converts an object like:
  {
    a: 1,
    b: 2
  }
  or 
  [
    {
      a: 1,
      b: 2
    }
  ]
  
  to

  [
    { key: 'a', value: 1 },
    { key: 'b', value: 2 }
  ]
  */
  if (!obj) {
    return [];
  }
  if (Array.isArray(obj)) {
    return obj.flatMap((item) =>
      reduce(
        item as Record<string, unknown>,
        (result: KvPair[], value, key) => {
          result.push({
            key: transformers.transformKey(key),
            value: transformers.transformValue(value),
          });
          return result;
        },
        []
      )
    );
  } else {
    return reduce(
      obj as Record<string, unknown>,
      (result: KvPair[], value, key) => {
        result.push({
          key: transformers.transformKey(key),
          value: transformers.transformValue(value),
        });
        return result;
      },
      []
    );
  }
};
